import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Alert,
  Grid,
  MenuItem,
  Card,
  CardContent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  CODIGOS_RETENCION_MH,
  calculateRetentionAmounts,
  TIPOS_DOCUMENTO_RELACIONADO,
  TIPOS_GENERACION,
  FORMAS_PAGO,
  METODOS_PAGO,
} from '../config/dteConfig';

/**
 * Crea un nuevo item vacío para CR (referencia a documento)
 * Según el schema JSON fe-cr-v2.json:
 * - numeroDocumento es siempre requerido
 * - tipoGeneracion determina el formato del documento relacionado
 */
const createEmptyCRItem = (numItem) => ({
  numItem,
  // Campos específicos de CR (referencia a otro documento)
  tipoDte: '01', // Por defecto: Factura Electrónica
  tipoGeneracion: 1, // Por defecto: 1 = Interno
  numeroDocumento: '', // Número de documento relacionado (siempre requerido)
  fechaEmision: new Date().toISOString().split('T')[0], // Fecha actual
  montoFacturaOriginal: null, // Total de la FE con IVA incluido para código 22
  montoSujetoGrav: null, // Monto sujeto a retención (null para evitar "0" a la izquierda)
  codigoRetencionMH: '22', // Por defecto: 22 (Rentas laborales)
  porcentajeEspecial: null, // Porcentaje configurable para C9
  ivaRetenido: null, // IVA retenido (null para evitar "0" a la izquierda)
  descripcion: '', // Descripción de la retención
});

/**
 * Componente de detalle para CR (Comprobante de Retención)
 * Este tipo de documento referencia otros documentos (FE, CCF, NRE)
 * Paso 2 del wizard
 * 
 * @param {object} document - Documento completo del wizard
 * @param {function} setDocument - Función para actualizar el documento
 * @param {function} handleAddItem - Función para agregar item
 * @param {function} handleRemoveItem - Función para eliminar item
 * @param {function} handleItemChange - Función para actualizar item
 * @param {function} handleHeaderChange - Función para actualizar header
 * @param {object} errors - Errores de validación
 * @param {boolean} isMobile - Si es vista móvil
 * @param {array} branches - Lista de sucursales
 * @param {object} expandedSections - Secciones expandidas
 * @param {function} setExpandedSections - Función para actualizar secciones expandidas
 */
const StepDetailReferencia = ({
  document,
  setDocument,
  handleAddItem,
  handleRemoveItem,
  handleItemChange,
  handleHeaderChange,
  errors = {},
  isMobile = false,
  branches = [],
  expandedSections = {},
  setExpandedSections = () => {},
}) => {
  const [localErrors, setLocalErrors] = useState({});
  
  // Obtener cuerpoDocumento del documento
  const cuerpoDocumento = document?.cuerpoDocumento || [];
  
  // Funciones internas que usan las props del wizard
  const internalHandleAddItem = () => {
    // Llamar a la función del wizard que ya tiene la lógica de createNewItem
    handleAddItem();
  };
  
  const internalHandleRemoveItem = (index) => {
    handleRemoveItem(index);
  };
  
  const internalHandleItemChange = (index, field, value) => {
    // Llamar directamente al handler del wizard
    handleItemChange(index, field, value);
  };

  // Formatear número de documento según el tipo
  // tipoGeneracion = 1 (Interno): NIT (14 dígitos) o DUI (9 dígitos)
  // tipoGeneracion = 2 (Electrónico): UUID como 2D796030-054C-4272-BCAA-322394EA37BB
  const formatNumeroDocumento = (value, tipoGeneracion) => {
    if (tipoGeneracion === 2) {
      // Electrónico: formatear como UUID (mayúsculas, guiones en posiciones correctas)
      const clean = value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 32).toUpperCase();
      // Formato: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
      return clean.replace(/([0-9A-F]{8})([0-9A-F]{0,4})?([0-9A-F]{0,4})?([0-9A-F]{0,4})?([0-9A-F]{0,12})?/, (m, g1, g2, g3, g4, g5) => {
        let result = g1;
        if (g2) result += '-' + g2;
        if (g3) result += '-' + g3;
        if (g4) result += '-' + g4;
        if (g5) result += '-' + g5;
        return result;
      });
    }
    
    // Interno: solo dígitos, máximo 14
    const digits = value.replace(/[^0-9]/g, '').slice(0, 14);
    
    // Si son 14 dígitos, formatear como NIT: ####-######-###-#
    if (digits.length === 14) {
      return digits.replace(/(\d{4})(\d{6})(\d{3})(\d{1})/, '$1-$2-$3-$4');
    }
    
    // Si son 9 dígitos, formatear como DUI: ########-#
    if (digits.length >= 9) {
      return digits.slice(0, 9).replace(/(\d{8})(\d{1})/, '$1-$2');
    }
    
    return digits;
  };

  const updateCalculatedRetention = (index, item, changes) => {
    const nextItem = { ...item, ...changes };
    Object.entries(changes).forEach(([field, value]) => {
      internalHandleItemChange(index, field, value);
    });
    const isCode22 = nextItem.codigoRetencionMH === '22';
    const changedOriginalAmount = Object.prototype.hasOwnProperty.call(changes, 'montoFacturaOriginal');
    const montoIngresado = isCode22
      ? (changedOriginalAmount
          ? changes.montoFacturaOriginal
          : nextItem.montoFacturaOriginal ?? ((Number(nextItem.montoSujetoGrav) || 0) * 1.13))
      : nextItem.montoSujetoGrav;
    const { montoSujetoGrav, ivaRetenido } = calculateRetentionAmounts(
      montoIngresado,
      nextItem.codigoRetencionMH,
      nextItem.porcentajeEspecial,
      { montoIncluyeIva: isCode22 },
    );
    internalHandleItemChange(index, 'montoSujetoGrav', montoSujetoGrav);
    internalHandleItemChange(index, 'ivaRetenido', ivaRetenido);
  };

  const getDisplayedAmount = (item) => {
    if (item.codigoRetencionMH !== '22') return item.montoSujetoGrav ?? '';
    if (item.montoFacturaOriginal !== null && item.montoFacturaOriginal !== undefined) {
      return item.montoFacturaOriginal;
    }
    const base = Number(item.montoSujetoGrav) || 0;
    return base > 0 ? Math.round(base * 1.13 * 100) / 100 : '';
  };

  return (
    <Box sx={{ mt: 2, overflowX: 'auto' }}>
      {/* Accordion de Datos del Documento (Header) - Collapsed por defecto */}
      <Accordion 
        expanded={expandedSections.header} 
        onChange={() => setExpandedSections(prev => ({ ...prev, header: !prev.header }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="bold">
            Datos del Documento
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Fecha de Emisión */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Fecha de Emisión"
                type="date"
                value={document.header?.fechaEmision || new Date().toISOString().split('T')[0]}
                onChange={(e) => handleHeaderChange('fechaEmision', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0]
                }}
              />
            </Grid>

            {/* Forma de Pago */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Pago</InputLabel>
                <Select
                  value={document.header?.formaPago || 'contado'}
                  label="Forma de Pago"
                  onChange={(e) => handleHeaderChange('formaPago', e.target.value)}
                >
                  {FORMAS_PAGO.map((fp) => (
                    <MenuItem key={fp.code} value={fp.code}>
                      {fp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Método de Pago */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Método de Pago</InputLabel>
                <Select
                  value={document.header?.metodoPago || '01'}
                  label="Método de Pago"
                  onChange={(e) => handleHeaderChange('metodoPago', e.target.value)}
                >
                  {METODOS_PAGO.map((mp) => (
                    <MenuItem key={mp.code} value={mp.code}>
                      {mp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sucursal */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sucursal</InputLabel>
                <Select
                  value={document.header?.sucursal || ''}
                  label="Sucursal"
                  onChange={(e) => handleHeaderChange('sucursal', e.target.value)}
                >
                  {branches.length > 0 ? (
                    branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.codigo_establecimiento}>
                        {branch.branch_name} ({branch.codigo_establecimiento})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="0">Matriz</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Detalle de Retenciones
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={internalHandleAddItem}
        >
          Agregar Retención
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Los comprobantes de retención referencia otros documentos (Facturas, CCF, etc.) y especifican el monto a retener.
      </Alert>

      {errors['cuerpoDocumento'] && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors['cuerpoDocumento']}
        </Alert>
      )}

      {cuerpoDocumento.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cuerpoDocumento.map((item, index) => (
            <Card key={index} sx={{ p: 2 }}>
              <Grid container spacing={1}>
                {/* Fila 1: # línea, Tipo doc, Tipo gen, número documento, fecha */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ minWidth: 24, fontWeight: 'bold' }}>#{index + 1}</Typography>
                    
                    {/* Tipo de Documento Relacionado */}
                    <TextField
                      select
                      size="small"
                      label="Tipo Doc."
                      value={item.tipoDte || '01'}
                      onChange={(e) => internalHandleItemChange(index, 'tipoDte', e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 130 }}
                    >
                      {TIPOS_DOCUMENTO_RELACIONADO.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.name}
                        </option>
                      ))}
                    </TextField>
                    
                    {/* Tipo de Generación */}
                    <TextField
                      select
                      size="small"
                      label="Tipo Gen."
                      value={item.tipoGeneracion || 1}
                      onChange={(e) => internalHandleItemChange(index, 'tipoGeneracion', parseInt(e.target.value))}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 100 }}
                    >
                      {TIPOS_GENERACION.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.name}
                        </option>
                      ))}
                    </TextField>
                    
                    {/* Número de Documento */}
                    <TextField
                      size="small"
                      label="Número Documento"
                      value={item.numeroDocumento || ''}
                      onChange={(e) => internalHandleItemChange(index, 'numeroDocumento', formatNumeroDocumento(e.target.value, item.tipoGeneracion))}
                      placeholder={item.tipoGeneracion === 2 ? '2D796030-054C-4272-BCAA-322394EA37BB' : '1234-567890-123-4'}
                      inputProps={{ maxLength: item.tipoGeneracion === 2 ? 36 : 17 }}
                      sx={{ flex: 1, minWidth: 150 }}
                      error={!!errors[`cuerpoDocumento.${index}.numeroDocumento`]}
                      helperText={errors[`cuerpoDocumento.${index}.numeroDocumento`] || ''}
                    />
                    
                    {/* Fecha de Emisión */}
                    <TextField
                      type="date"
                      size="small"
                      label="Fecha"
                      value={item.fechaEmision || ''}
                      onChange={(e) => internalHandleItemChange(index, 'fechaEmision', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 120 }}
                    />
                  </Box>
                </Grid>

                {/* Fila 2: Monto sujeto, Código retención, IVA retenido, botón eliminar */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Para código 22 se captura el total de la FE con IVA incluido. */}
                    <TextField
                      type="number"
                      size="small"
                      label={item.codigoRetencionMH === '22' ? 'Factura original (con IVA)' : 'Monto Sujeto'}
                      value={getDisplayedAmount(item)}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '.' || !isNaN(parseFloat(value))) {
                          const numericValue = value === '' ? null : parseFloat(value);
                          updateCalculatedRetention(
                            index,
                            item,
                            item.codigoRetencionMH === '22'
                              ? { montoFacturaOriginal: numericValue }
                              : { montoSujetoGrav: numericValue },
                          );
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (isNaN(value) || value < 0) {
                          updateCalculatedRetention(
                            index,
                            item,
                            item.codigoRetencionMH === '22'
                              ? { montoFacturaOriginal: null }
                              : { montoSujetoGrav: null },
                          );
                        }
                      }}
                      inputProps={{ step: 0.01, min: 0 }}
                      sx={{ minWidth: 110 }}
                      error={!!errors[`cuerpoDocumento.${index}.montoSujetoGrav`]}
                      helperText={errors[`cuerpoDocumento.${index}.montoSujetoGrav`] || ''}
                    />

                    {item.codigoRetencionMH === '22' && (
                      <TextField
                        type="number"
                        size="small"
                        label="Monto Sujeto"
                        value={item.montoSujetoGrav ?? ''}
                        InputProps={{ readOnly: true }}
                        inputProps={{ step: 0.01, min: 0, tabIndex: -1 }}
                        sx={{ minWidth: 125 }}
                        helperText="Base sin IVA"
                      />
                    )}
                    
                    {/* Código de Retención MH */}
                    <TextField
                      select
                      size="small"
                      label="Cód. Retención"
                      value={item.codigoRetencionMH || '22'}
                      onChange={(e) => {
                        updateCalculatedRetention(index, item, {
                          codigoRetencionMH: e.target.value,
                        });
                      }}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 180 }}
                    >
                      {CODIGOS_RETENCION_MH.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.name}
                        </option>
                      ))}
                    </TextField>

                    {item.codigoRetencionMH === 'C9' && (
                      <TextField
                        type="number"
                        size="small"
                        label="Porcentaje especial"
                        value={item.porcentajeEspecial ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || value === '.' || !isNaN(parseFloat(value))) {
                            updateCalculatedRetention(index, item, {
                              porcentajeEspecial: value === '' ? null : parseFloat(value),
                            });
                          }
                        }}
                        inputProps={{ step: 0.01, min: 0.01, max: 100 }}
                        InputProps={{ endAdornment: <Typography color="text.secondary">%</Typography> }}
                        sx={{ minWidth: 150 }}
                        error={!!errors[`cuerpoDocumento.${index}.porcentajeEspecial`]}
                        helperText={errors[`cuerpoDocumento.${index}.porcentajeEspecial`] || ''}
                      />
                    )}
                    
                    {/* IVA Retenido */}
                    <TextField
                      type="number"
                      size="small"
                      label="IVA Retenido"
                      value={item.ivaRetenido ?? ''}
                      InputProps={{ readOnly: true }}
                      inputProps={{ step: 0.01, min: 0, tabIndex: -1 }}
                      sx={{ minWidth: 100 }}
                    />
                    
                    {/* Botón eliminar */}
                    <IconButton 
                      color="error" 
                      onClick={() => internalHandleRemoveItem(index)}
                      disabled={cuerpoDocumento.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>

                {/* Fila 3: Descripción */}
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Descripción"
                    value={item.descripcion || ''}
                    onChange={(e) => internalHandleItemChange(index, 'descripcion', e.target.value)}
                    placeholder="Descripción de la retención"
                    inputProps={{ maxLength: 1000 }}
                    fullWidth
                    error={!!errors[`cuerpoDocumento.${index}.descripcion`]}
                    helperText={errors[`cuerpoDocumento.${index}.descripcion`] || 'Descripción requerida según schema MH'}
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          No hay retenciones agregadas. Haga clic en "Agregar Retención" para comenzar.
        </Alert>
      )}
      
      {/* Totales */}
      {cuerpoDocumento.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Monto Sujeto a Retención:
              </Typography>
              <Typography variant="h6">
                ${cuerpoDocumento.reduce((sum, item) => sum + (parseFloat(item.montoSujetoGrav) || 0), 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Total IVA Retenido:
              </Typography>
              <Typography variant="h6">
                ${cuerpoDocumento.reduce((sum, item) => sum + (parseFloat(item.ivaRetenido) || 0), 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Items:
              </Typography>
              <Typography variant="h6">
                {cuerpoDocumento.length}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default StepDetailReferencia;
